import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Box, Button, CheckBox, DataTable, Layer, Pagination, Spinner } from 'grommet';
import { Close, StatusWarning } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { LastActivePeriodSelect } from '../forms/LastActivePeriodSelect';
import { SearchInput } from '../forms/SearchInput';
import { ApiError, FacilityService } from '/src/api';
import {
  AddButton,
  DataTableHeader,
  DataTableItem,
  EntityIcon,
  FormCard,
  InfoBox,
  Link,
  RetirementReasonSelect,
  RetirementReasonTextField,
  Text,
} from '/src/components';
import { useGlobalStore, useUserStore } from '/src/context';
import { Facility, ModelName, TClientId, TFacilityId, TRetirementReasonId } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { DateString, EntityStatus, TResponseMetadata } from '/src/lib/types';
import { getPeriodFromDateString } from '/src/utils';

export type RetireMultipleFacilitiesModalProps = {
  clientId: TClientId;
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  setShouldRefresh: (shouldRefresh: boolean) => void;
  isCanada?: boolean;
};

export const RetireMultipleFacilitiesModal: React.FC<RetireMultipleFacilitiesModalProps> = observer(
  ({ clientId, isVisible, setIsVisible, setShouldRefresh, isCanada = false }) => {
    /** Context **/
    const userStore = useUserStore();
    const globalStore = useGlobalStore();
    const { t } = useTranslation();

    /** State **/
    const [isLoading, setIsLoading] = useState(false);
    const [isRetiring, setIsRetiring] = useState(false);
    const [isSelectingAll, setIsSelectingAll] = useState(false);
    const [facilitiesList, setFacilitiesList] = useState<Facility[]>([]);
    const [selectedFacilityIds, setSelectedFacilityIds] = useState<TFacilityId[]>([]);
    const [lastActivePeriod, setLastActivePeriod] = useState<DateString>('');
    const [reasonId, setReasonId] = useState<TRetirementReasonId>();
    const [reasonText, setReasonText] = useState('');
    const [isOther, setIsOther] = useState(false);
    const [isConfirmed1, setIsConfirmed1] = useState(false);
    const [isConfirmed2, setIsConfirmed2] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(20);
    const [metadata, setMetadata] = useState<TResponseMetadata>();
    const [earliestFirstActive, setEarliestFirstActive] = useState<DateString>();
    const [searchQuery, setSearchQuery] = useState('');
    const [committedSearchQuery, setCommittedSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const selectAllRequestIdRef = useRef(0);

    /** Computed **/
    const canRetire = userStore.isFuseAdmin;

    const selectedPeriodLabel = lastActivePeriod
      ? getPeriodFromDateString(lastActivePeriod, isCanada)
      : t('common:theLastReportingPeriod');

    const hasReason = !!reasonId && (!isOther || !!reasonText.trim());

    const selectedFacilityIdSet = new Set(selectedFacilityIds);
    const selectedOnPageCount = facilitiesList.filter((facility) => selectedFacilityIdSet.has(facility.id)).length;
    const isPageSelected = !!facilitiesList.length && selectedOnPageCount === facilitiesList.length;
    const isPagePartiallySelected = selectedOnPageCount > 0 && selectedOnPageCount < facilitiesList.length;
    const hasSelection = !!selectedFacilityIds.length;

    /** Methods **/
    const search = (query: string) => {
      setSearchQuery(query);
      setCommittedSearchQuery(query);
      setIsSearching(query !== committedSearchQuery);
      setCurrentPage(1);
    };

    const handlePeriodChange = (period: DateString) => {
      selectAllRequestIdRef.current += 1;
      setSelectedFacilityIds([]);
      setIsConfirmed1(false);
      setIsConfirmed2(false);
      setCurrentPage(1);
      setLastActivePeriod(period);
    };

    const loadFacilities = async (page = 1, address?: string) => {
      try {
        setIsLoading(true);
        const { data, meta, earliest_first_active_reporting_quarter } = await FacilityService.list({
          client_id: clientId,
          is_active: true,
          page,
          limit,
          ...(lastActivePeriod ? { active_by_reporting_quarter: lastActivePeriod } : {}),
          ...(address ? { address_line1: address } : {}),
        });

        setFacilitiesList(data);
        setMetadata(meta);
        setCurrentPage(meta?.current_page ?? page);
        setEarliestFirstActive(earliest_first_active_reporting_quarter ?? undefined);
      } catch (err) {
        globalStore.handleApiError(err as ApiError, toastMessages.listFacilities.error);
      } finally {
        setIsLoading(false);
        setIsSearching(false);
      }
    };

    const retireFacilities = async () => {
      if (
        isRetiring ||
        !selectedFacilityIds.length ||
        !lastActivePeriod ||
        !reasonId ||
        (isOther && !reasonText.trim())
      )
        return;

      try {
        setIsRetiring(true);
        await FacilityService.retire({
          facility_ids: selectedFacilityIds,
          last_active_reporting_quarter: lastActivePeriod,
          retirement_reason_id: reasonId,
          retirement_reason_other: isOther ? reasonText.trim() : null,
        });
        setShouldRefresh(true);
        setIsVisible(false);
        toast.success(toastMessages.retireFacility.success);
      } catch (err) {
        globalStore.handleApiError(err as ApiError, toastMessages.retireFacility.error);
      } finally {
        setIsRetiring(false);
      }
    };

    const toggleSelection = (facilityId: TFacilityId) => {
      setSelectedFacilityIds((prev) =>
        prev.includes(facilityId) ? prev.filter((id) => id !== facilityId) : [...prev, facilityId]
      );
    };

    const toggleSelectPage = (checked: boolean) => {
      const pageIds = facilitiesList.map((facility) => facility.id);
      const pageIdSet = new Set(pageIds);
      setSelectedFacilityIds((prev) => {
        const offPageIds = prev.filter((id) => !pageIdSet.has(id));
        return checked ? [...offPageIds, ...pageIds] : offPageIds;
      });
    };

    const selectAll = async () => {
      if (isSelectingAll || !metadata?.total) return;

      selectAllRequestIdRef.current += 1;
      const requestId = selectAllRequestIdRef.current;

      try {
        setIsSelectingAll(true);
        const { data } = await FacilityService.list({
          client_id: clientId,
          is_active: true,
          page: 1,
          limit: metadata.total,
          ...(lastActivePeriod ? { active_by_reporting_quarter: lastActivePeriod } : {}),
          ...(committedSearchQuery ? { address_line1: committedSearchQuery } : {}),
        });

        if (requestId !== selectAllRequestIdRef.current) return;

        setSelectedFacilityIds((prev) => Array.from(new Set([...prev, ...data.map((facility) => facility.id)])));
      } catch (err) {
        globalStore.handleApiError(err as ApiError, toastMessages.listFacilities.error);
      } finally {
        setIsSelectingAll(false);
      }
    };

    /** Effects **/
    useEffect(() => {
      if (isVisible) {
        setLastActivePeriod('');
        setReasonId(undefined);
        setReasonText('');
        setIsOther(false);
        setSelectedFacilityIds([]);
        setIsConfirmed1(false);
        setIsConfirmed2(false);
        setCurrentPage(1);
        setSearchQuery('');
        setCommittedSearchQuery('');
      }
    }, [isVisible, clientId]);

    useEffect(() => {
      if (!canRetire && isVisible) setIsVisible(false);
    }, [canRetire, isVisible]);

    useEffect(() => {
      if (isVisible && currentPage) {
        loadFacilities(currentPage, committedSearchQuery || undefined);
      }
    }, [isVisible, currentPage, committedSearchQuery, lastActivePeriod]);

    /** Render **/
    const renderDataTable = () => {
      return (
        <Box elevation="small" round="xsmall" overflow="hidden">
          <DataTable
            pad={{ horizontal: 'small', vertical: 'xsmall' }}
            primaryKey="id"
            columns={[
              {
                property: 'select',
                size: '6%',
                header: (
                  <CheckBox
                    checked={isPageSelected}
                    indeterminate={isPagePartiallySelected}
                    onChange={(e) => toggleSelectPage(e.target.checked)}
                  />
                ),
                render: (facility: Facility) => (
                  <CheckBox
                    checked={selectedFacilityIdSet.has(facility.id)}
                    onChange={() => toggleSelection(facility.id)}
                  />
                ),
              },
              {
                property: 'address_line1',
                size: '30%',
                header: <DataTableHeader title={t('common:address')} />,
                render: (facility: Facility) => (
                  <Box pad={{ vertical: 'small' }}>
                    <Link to={`/clients/${clientId}/facilities/${facility.id}`}>
                      <Box>
                        <Text size="medium" weight={700} color="accent-1" fontFamily="Lato, sans-serif">
                          {facility.address_line1}
                        </Text>
                        <Text size="small" color="accent-1" fontFamily="Lato, sans-serif">
                          {facility.address_city},{' '}
                          {
                            (facility.address_region ?? globalStore.getRegionById(facility.address_region_id))
                              ?.short_code
                          }{' '}
                          {facility.address_post_code}
                        </Text>
                      </Box>
                    </Link>
                  </Box>
                ),
              },
              {
                property: 'is_active',
                align: 'center',
                header: <DataTableHeader title={t('common:status')} />,
                render: (facility: Facility) => (
                  <DataTableItem value={facility.is_active ? EntityStatus.Active : EntityStatus.Retired} />
                ),
              },
              {
                property: 'program.name',
                size: '15%',
                header: <DataTableHeader title={t('common:program')} />,
                render: (facility: Facility) => <DataTableItem value={facility.program?.name || '-'} />,
              },
              {
                property: 'utility.name',
                size: '25%',
                header: <DataTableHeader title={t('facility:utility')} />,
                render: (facility: Facility) => <DataTableItem value={facility.utility?.name || '-'} />,
              },
            ]}
            data={facilitiesList}
            sortable
            background={['light-6', 'white']}
            border={{ color: 'light-2', side: 'bottom', size: 'small' }}
          />
        </Box>
      );
    };

    return isVisible ? (
      <Layer
        position="center"
        onEsc={() => setIsVisible(false)}
        onClickOutside={() => setIsVisible(false)}
        responsive={false}
        style={{
          width: '95%',
          maxWidth: '1400px',
          maxHeight: '95vh',
          overflow: 'auto',
          borderRadius: '8px',
        }}
      >
        <FormCard
          width="100%"
          title={t('facility:retireFacilities')}
          icon={<EntityIcon entityName={ModelName.Facility} />}
          onSubmit={() => retireFacilities()}
          isLoading={isLoading || isRetiring}
          hideRequiredText
          saveButtonLabel={t('common:retireButton')}
          saveButtonLoadingLabel={t('common:retiringButton')}
          saveButtonDisabled={
            !isConfirmed1 || !isConfirmed2 || !selectedFacilityIds.length || !lastActivePeriod || !hasReason
          }
          headerChildren={[
            <Button
              key="close"
              pad="none"
              onClick={() => setIsVisible(false)}
              icon={<Close size="20px" />}
              tip={t('common:close')}
            />,
          ]}
        >
          <Box pad={{ horizontal: 'medium', bottom: 'medium' }} gap="medium">
            <InfoBox gap="1rem" direction="row" align="center">
              <StatusWarning size="medium" color="status-warning" />
              <Text>
                {t('facility:retireWarning')} <b>{t('common:after')}</b> {selectedPeriodLabel}{' '}
                <b>{t('facility:retireWarningEnd')}</b>
              </Text>
            </InfoBox>
            <Box direction="row" align="end" justify="between">
              <Box direction="row" align="end" gap="small" width="40%">
                <LastActivePeriodSelect
                  id="last_active_reporting_quarter"
                  label={t('common:lastReportingPeriod')}
                  placeholder={isLoading ? t('common:loading') : t('common:selectPeriod')}
                  value={lastActivePeriod}
                  setValue={(value) => handlePeriodChange(value)}
                  clientId={clientId}
                  programId={facilitiesList[0]?.program_id}
                  reportingPeriodTypeId={facilitiesList[0]?.reporting_period_type_id}
                  firstActivePeriod={earliestFirstActive}
                  required
                  width={'60%'}
                />
                <RetirementReasonSelect
                  entityType="facility"
                  showTextField={false}
                  fill="horizontal"
                  reasonId={reasonId}
                  setReasonId={setReasonId}
                  setReasonText={setReasonText}
                  setIsOther={setIsOther}
                />
              </Box>
              <Box direction="row" align="center" gap="small">
                {hasSelection && (
                  <Text size="small">{t('common:selectedCount', { count: selectedFacilityIds.length })}</Text>
                )}
                {(hasSelection || !!metadata?.total) && (
                  <AddButton
                    label={hasSelection ? t('common:clearAll') : `${t('common:selectAll')} (${metadata?.total})`}
                    onClick={() => {
                      setIsConfirmed1(false);
                      setIsConfirmed2(false);
                      if (hasSelection) setSelectedFacilityIds([]);
                      else selectAll();
                    }}
                    disabled={isSelectingAll || isLoading || isSearching}
                  />
                )}
                <SearchInput
                  searchQuery={searchQuery}
                  placeholder={t('facility:searchByAddress', 'Search by Address')}
                  setSearchQuery={setSearchQuery}
                  onSearch={search}
                  isSearching={isSearching}
                />
              </Box>
            </Box>
            {isOther && <RetirementReasonTextField value={reasonText} setValue={setReasonText} />}
            {lastActivePeriod && hasReason && hasSelection && (
              <Box gap="small">
                <InfoBox
                  gap="1rem"
                  direction="row"
                  align="center"
                  background={{ color: 'status-error', dark: true }}
                  pad="medium"
                  round="small"
                >
                  <CheckBox checked={isConfirmed1} onChange={() => setIsConfirmed1(!isConfirmed1)} color="white" />
                  <Text color="white">
                    {t('facility:retireMultipleConfirmation', { count: selectedFacilityIds.length })}
                  </Text>
                </InfoBox>
                <InfoBox
                  gap="1rem"
                  direction="row"
                  align="center"
                  background={{ color: 'status-error', dark: true }}
                  pad="medium"
                  round="small"
                >
                  <CheckBox checked={isConfirmed2} onChange={() => setIsConfirmed2(!isConfirmed2)} color="white" />
                  <Text color="white">
                    {t('facility:retireConfirmation2Start')} <b>{t('common:after')}</b> {selectedPeriodLabel}{' '}
                    {t('facility:retireConfirmation2End')}
                  </Text>
                </InfoBox>
              </Box>
            )}
            <Box gap="small">
              {isLoading ? (
                <Box pad={{ vertical: 'medium' }} height="10vh" fill="horizontal" justify="center" align="center">
                  <Spinner size="medium" />
                </Box>
              ) : facilitiesList.length ? (
                <>
                  {renderDataTable()}
                  {!!metadata && metadata.total > limit && (
                    <Box pad={{ top: 'medium' }} justify="center" align="center">
                      <Pagination
                        alignSelf="center"
                        size="small"
                        page={currentPage}
                        step={metadata.per_page}
                        numberItems={metadata.total}
                        onChange={(e) => setCurrentPage(e.page)}
                      />
                    </Box>
                  )}
                </>
              ) : (
                <Box pad={{ horizontal: 'small', vertical: 'medium' }} background="light-6" align="center">
                  <Text size="medium" fontFamily="Lato, sans-serif">
                    {t('facility:noActiveFacilitiesFound', 'No active facilities found.')}
                  </Text>
                </Box>
              )}
            </Box>
          </Box>
        </FormCard>
      </Layer>
    ) : (
      <></>
    );
  }
);
